import type {
  MaintenanceSlaInterventionMode,
  MaintenanceSlaScopeInterventionMode,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';

const ON_SITE_CODES = new Set(['N1', 'N2', 'N3', 'N4', 'N5']);
const REMOTE_CODES = new Set(['R1', 'R2', 'R3', 'R4', 'R5']);

const INTERVENTION_MODE_LABEL_KEYS: Record<
  MaintenanceSlaInterventionMode,
  string
> = {
  ONSITE: 'On-site',
  REMOTE: 'Remote',
  BOTH: 'On-site + remote',
};

const SCOPE_INTERVENTION_MODE_LABEL_KEYS: Record<
  MaintenanceSlaScopeInterventionMode,
  string
> = {
  ONSITE: 'On-site',
  REMOTE: 'Remote',
};

export function interventionModeLabelKey(
  mode: MaintenanceSlaInterventionMode,
): string {
  return INTERVENTION_MODE_LABEL_KEYS[mode];
}

export function scopeInterventionModeLabelKey(
  mode: MaintenanceSlaScopeInterventionMode,
): string {
  return SCOPE_INTERVENTION_MODE_LABEL_KEYS[mode];
}

export function levelCodeMatchesCategory(
  code: string,
  category: 'ON_SITE' | 'REMOTE',
): boolean {
  if (category === 'ON_SITE') {
    return ON_SITE_CODES.has(code);
  }

  return REMOTE_CODES.has(code);
}

export function isLevelInAnotherGroup(
  groups: ReadonlyArray<{
    id: string;
    levels: ReadonlyArray<{ code: string }>;
  }>,
  groupId: string,
  levelCode: string,
): boolean {
  return groups.some(
    (group) =>
      group.id !== groupId &&
      group.levels.some((level) => level.code === levelCode),
  );
}

export function durationPartsToMinutes(
  hours: number | null,
  minutes: number | null,
): number | null {
  if (hours == null && minutes == null) {
    return null;
  }

  const hourPart = hours ?? 0;
  const minutePart = minutes ?? 0;

  if (!Number.isInteger(hourPart) || hourPart < 0) {
    return null;
  }

  if (!Number.isInteger(minutePart) || minutePart < 0 || minutePart > 59) {
    return null;
  }

  if (hourPart === 0 && minutePart === 0) {
    return null;
  }

  return hourPart * 60 + minutePart;
}

export function minutesToDurationParts(totalMinutes: number): {
  hours: number;
  minutes: number;
} {
  const safe = Math.max(0, Math.trunc(totalMinutes));

  return {
    hours: Math.floor(safe / 60),
    minutes: safe % 60,
  };
}
